import React, { useEffect, useState } from "react";
import Modal from "react-bootstrap/Modal";
import { Button, Spinner } from "react-bootstrap";
import AddProducerDetails from "./AddProducerDetails";
import AddProducerConfirmation from "./AddProducerConfirmation";
import { Producer, getProducerDisplayName } from "./producer";
import ValidationError from "common/validationError";
import _ from "lodash";
import ConfirmationModal, { useConfirmationModal } from "common/ui/ConfirmationModal";
import producerService from "./producerService";
import ErrorList from "common/ui/ErrorList";
import { localizeProducer as localize } from "features/localization/localizeService";

export type AddProducerProps = {
  producer: any;
  showEditMode: boolean;
  hideDelete?: boolean;
  handleCancel: () => void;
  handleSubmit: (producer: Producer) => Promise<any>;
  showDeleteModal: () => void;
  showWarnings?: boolean;
};

const AddProducerModal:  React.FC<AddProducerProps> = props => {
  
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [producer, setProducer]= useState<Producer>(props.producer);
  const [validated, setValidated] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const [accountWarnings, setAccountWarnings] = useState<string[]>(null);
  const [showWarningConfirmation, setShowWarningConfirmation] = useState(false);

  const confirmModal = useConfirmationModal();
  var modalTitle: string = "";

  

  useEffect(() => {
    if(accountWarnings == null) return;

    if(accountWarnings.length > 0) {
      setShowWarningConfirmation(true);
      confirmModal.show(
        localize("AddProducerModal_Header_Warnings"),
        <ul>
          {accountWarnings.map((w, i) => <li key={i}>{w}</li>)}
        </ul>,
        () => {
          setShowWarningConfirmation(false); 
          setShowConfirmation(true);
        },
        () => {
          setShowWarningConfirmation(false); 
          setShowConfirmation(false);
        }
      );
    }
    else
    {
      setShowConfirmation(true);
    }
}, [accountWarnings])
   
  if (!showConfirmation) {
    modalTitle = props.showEditMode ? 
    localize("AddProducerModal_Header_EditCustomer") : 
    localize("AddProducerModal_Header_AddCustomer");
  } else {
    modalTitle = props.showEditMode
      ? localize("AddProducerModal_Header_ReviewEditCustomer")
      : localize("AddProducerModal_Header_ReviewNewCustomer");
  }

  function goBack() {
    setShowConfirmation(false); 
    setIsSubmitting(false);
    setErrors([]);
  }

  async function goNext(event:any) {
    const producerForm = event.currentTarget;  
    if (producerForm.checkValidity() === false) {
      event.preventDefault();
      event.stopPropagation();
    }
    else {
      await setWarnings(event);
    }
    setValidated(true);
  }

  async function handleSubmit() {
    setIsSubmitting(true);
    try {
      await props.handleSubmit(producer);
      setShowConfirmation(false);
      setIsSubmitting(false);
      setShowWarningConfirmation(false);
    } catch (err) {
      setIsSubmitting(false);
      setErrors((err as ValidationError).errors);
    }
  }

  function handleAbort() {
    setShowConfirmation(false);
    setIsSubmitting(false);
    setShowWarningConfirmation(false);
    props.handleCancel();
  }

  function onProducerUpdated(producer: Producer): void {
    producer.accountIds = producer.accounts.map(c => c.accountId);
    setProducer(producer);
  }

  async function setWarnings(event: any) {
    if(!props.showWarnings && getProducerDisplayName(producer) != "" && producer.accountIds.length > 0) {
      setAccountWarnings([]);
    }

    event.preventDefault();
    event.stopPropagation();

    await producerService.validateAccounts(producer)
      .then((x: string[]) => {
        setAccountWarnings(x);
      }).catch(err => {
        setErrors((err as ValidationError).errors);
      });
  }

    return (
      <Modal show onHide={() => handleAbort()} backdrop="static" size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{modalTitle}</Modal.Title>
        </Modal.Header>

        {!showConfirmation && !showWarningConfirmation && (
          <>
            <Modal.Body>
              <AddProducerDetails
                producer={ producer }
                handleNext={(ev: any) => goNext(ev)}
                validated={validated}
                handleCancel={ () => handleAbort() }
                showDeleteModal={() => props.showDeleteModal()}
                onProducerUpdated={(producer: Producer) => onProducerUpdated(producer)}
                showEditMode={props.showEditMode}
                hideDelete={props.hideDelete}
              />
              <ErrorList errors={errors} />
            </Modal.Body>
            
          </>
        )}
        <ConfirmationModal modalState={confirmModal.state} />
        {showConfirmation && (
          <>
            <Modal.Body className="add-offer-modal">
              {!isSubmitting && (
                <AddProducerConfirmation
                  producer={ producer }
                  errors={ errors}
                />
              )}
              {isSubmitting && <Spinner animation="border" />}
            </Modal.Body>
            <Modal.Footer className="align center">
              <Button
                variant="secondary"
                className="float-left"
                onClick={() => goBack()}
              >
                {localize("AddProducerModal_Button_Back")}
              </Button>
              <Button variant="secondary" onClick={() => handleAbort()}>
                {localize("AddProducerModal_Button_Cancel")}
              </Button>
              <Button variant="primary" onClick={() => handleSubmit()}>
                {props.showEditMode 
                  ? localize("AddProducerModal_Button_Update") 
                  : localize("AddProducerModal_Button_Create")}
              </Button>
            </Modal.Footer>
          </>
        )}
      </Modal>
    );
  }


export default AddProducerModal;